import logger from '../utils/logger';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Settings, LogOut } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';

export default function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSignOut = async () => {
    try {
      setIsOpen(false);
      await signOut();
      window.location.href = '/';
    } catch (error) {
      logger.error('Erreur déconnexion:', error);
      alert('Erreur lors de la déconnexion. Veuillez réessayer.');
    }
  };

  if (!user) return null;

  const initials = (profile?.full_name || user.email || 'C')
    .split(' ')
    .map((p: string) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 border border-theme-strong text-[11px] tracking-wider font-medium hover:bg-theme-hover"
        title="Menu utilisateur"
      >
        {initials}
      </button>

      {isOpen && createPortal(
        <div
          ref={menuRef}
          className="fixed w-64 bg-theme-surface border border-theme overflow-hidden"
          style={{
            top: `${buttonPosition.top}px`,
            right: `${buttonPosition.right}px`,
            zIndex: 999999
          }}
        >
          <div className="p-4 border-b border-theme">
            <p className="text-sm font-medium text-theme-primary truncate">
              {profile?.full_name || user.email}
            </p>
            <p className="text-xs text-theme-tertiary truncate mt-0.5">
              {user.email}
            </p>
          </div>

          <div className="p-1">
            <button
              onClick={() => { setIsOpen(false); navigate('/settings'); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-theme-primary hover:bg-theme-hover text-left"
            >
              <Settings size={15} strokeWidth={1.5} />
              <span className="text-sm">Paramètres</span>
            </button>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-theme-primary hover:bg-theme-hover text-left"
            >
              <LogOut size={15} strokeWidth={1.5} />
              <span className="text-sm">Déconnexion</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
